import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import GoogleAutocomplete from './AutoComplete';
import { usePlaceDetails } from '../../hooks/usePlaceDetails'; 
import { useTheme } from '../../context/ThemeContext';

import { 
  internationalCityFormatter,
} from './autocompleteFormatters';

import clsx from 'clsx';

const AddressField = ({ 
  id,
  types,
  currentAddress,
  placeholder,
  onAddressChange,
  label,
  className,
  containerClasses,
  required=false,
  disabled=false,
  formatValue
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [error, setError] = useState(null); 

  const [loadingPlaceDetails, setLoadingPlaceDetails] = useState(false);
  const [addressValue, setAddressValue] = useState(null);
  
  const { 
    fetchPlaceDetails, 
    error: placeDetailsError,
    validatePlaceDetails,
  } = usePlaceDetails();

  useEffect(() => {
    const color_hex = '#252525';
    window.gon.theme = {
      color: color_hex,
      primary: color_hex
    }
  }, [])

  useEffect(() => {
    setAddressValue(
      currentAddress? 
        formatValue?.(currentAddress) || currentAddress?.name ||
          currentAddress?.formatted_address : ''
    );
  }, [currentAddress]);

  /**
   * Enhances address data by fetching complete place details
   * @param {Object} address - Initial address object from autocomplete
   * @param {string} sessionToken - Optional session token for Google Places billing
   * @returns {Object} Enhanced address object with complete details
   */
  const loggingIn = async (address, sessionToken = null) => {
    if (loadingPlaceDetails) {
      console.warn('Already loading place details, skipping duplicate request');
      return address;
    }

    if (!address || (!address._id && !address.place.id && !address.prediction?.place_id)) {
      console.warn('No place ID found in address object:', address);
      return address;
    }
    const placeId = address._id || address.place.id || address.prediction?.place_id
    setLoadingPlaceDetails(true);
    
    try {

      // Fetch complete place details
      const placeDetails = await fetchPlaceDetails(placeId, sessionToken);

      // Validate the response
      const validation = validatePlaceDetails(placeDetails);
      if (!validation.isValid) {
        console.warn('Place details validation failed:', validation.message);
        // Continue with original address if validation fails
        return address;
      }

      // Create enhanced address object combining original autocomplete data with detailed place info
      const enhancedAddress = {
        // Keep original autocomplete structure
        ...address,
        ...placeDetails,
        place_details_fetched_at: new Date().toISOString(),
        enhanced_with_place_details: true,
      };
      
      return enhancedAddress;

    } catch (error) {
      console.error('Failed to enhance address with place details:', error);
      setError('Failed to fetch complete address details. Please try again.');
      // Return original address with error flag if place details fetch fails
      return {
        ...address,
        place_details_error: error.message,
        enhanced_with_place_details: false
      };
    } finally {
      setLoadingPlaceDetails(false);
    }
  };

  const handleClearAddress = useCallback(() => {
    setAddressValue('');
    onAddressChange?.(null);
  }, [onAddressChange]);
  
  /**
   * Handles address selection from autocomplete
   * @param {Object} address - Selected address from autocomplete
   * @param {string} sessionToken - Optional session token
   */
  const handleAddressSelect = useCallback((address, sessionToken = null) => {
    const enhanceAddress = async () => {
       setAddressValue(address.name || address.formatted_address);
      
      const enhanced = await loggingIn(address, sessionToken);
      if (formatValue) {
        setAddressValue(formatValue(enhanced));
      } else {
        setAddressValue(enhanced.name || enhanced.formatted_address);
      } 
      onAddressChange?.(enhanced);
    };
    try {
      enhanceAddress(); 
    } catch (error) {
      console.error('Error handling address selection:', error);
    }
  }, [onAddressChange]);

  const displayError = error || placeDetailsError;

  const inputClassName = clsx(
    'w-full text-sm px-3 py-3 border rounded-lg transition-colors',
    'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
    {
      'bg-slate-800 border-slate-600 text-slate-200 placeholder-slate-400': isDark && !displayError,
      'bg-white border-gray-300 text-gray-900 placeholder-gray-400': !isDark && !displayError,
      'border-red-500 bg-red-50': displayError && !isDark,
      'border-red-400 bg-slate-800': displayError && isDark,
      'opacity-50 cursor-not-allowed': disabled
    },
    className
  );

  const labelClassName = clsx(
    'block text-xs font-normal mb-2',
    {
      'text-slate-200': isDark,
      'text-gray-700': !isDark,
      'text-red-400': displayError && isDark,
      'text-red-600': displayError && !isDark
    }
  );

  const errorClassName = clsx(
    'text-xs mt-1',
    {
      'text-red-400': isDark,
      'text-red-600': !isDark
    }
  );

  return (
    <div className={containerClasses}>
      {label && (
        <label htmlFor={id} className={labelClassName}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <GoogleAutocomplete
        data-testid={id}
        apiKey={window.gon?.google_autocomplete_api_key || ''}
        placeholder={placeholder}
        customFormatter={internationalCityFormatter}
        types={types || ['(cities)']}
        value={addressValue || ''}
        onChange={(value) => {
          if(value?.length === 0)
            handleClearAddress();
          setAddressValue(value)
        }}
        onSelect={handleAddressSelect}
        className={className}
        inputClassName={inputClassName}
        aria-invalid={displayError ? 'true' : 'false'}
        aria-describedby={displayError ? `${id}-error` : undefined}
        disabled={disabled}
      />
      {displayError && (
        <div id={`${id}-error`} className={errorClassName}>
          {displayError}
        </div>
      )}
    </div>
  );
};

AddressField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  className: PropTypes.string,
  containerClasses: PropTypes.string,
  types: PropTypes.arrayOf(PropTypes.string),
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  formatValue: PropTypes.func,
  currentAddress: PropTypes.shape({
    name: PropTypes.string,
    formatted_address: PropTypes.string,
    street: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string,
    country: PropTypes.string,
    lat: PropTypes.number,
    lng: PropTypes.number
  }),

  placeholder: PropTypes.string,

  /** Optional callback when address changes */
  onAddressChange: PropTypes.func
};

export default AddressField;